import { SupabaseClient } from "@supabase/supabase-js";
import { v4 as uuidv4 } from "uuid";
import WorkFactory from "../workFactory";
import { Feedback } from "@/app/types/feedback";

/**
 * Insert a new feedback entry for a work.
 *
 * If only a location URL is provided, the function will attempt to resolve the
 * work's friendly ID from the URL.
 *
 * @param client - Supabase client
 * @param userId - Author of the feedback
 * @param feedback - Feedback content without database fields
 * @param getWorkByFriendlyId - Helper to find works by friendly ID
 * @returns The created feedback record
 */
// ID to use for the Anonymous User profile
export const ANONYMOUS_USER_ID = "3ef30510-7c9d-45d7-95a7-c090fb75f0f7";

export async function createFeedback(
  client: SupabaseClient,
  userId: string,
  feedback: Omit<Feedback, "id" | "user_id" | "created_at" | "updated_at">,
  getWorkByFriendlyId: (id: string) => Promise<any>
): Promise<Feedback> {
  let workId = feedback.work_id;
  if (!workId && feedback.location_page) {
    const match = feedback.location_page.match(/\/book\/([^\/]+)/);
    if (match && match[1]) {
      const workFriendlyId = match[1];
      try {
        const work = await getWorkByFriendlyId(workFriendlyId);
        if (work) {
          workId = work.id;
        }
      } catch (error) {
        console.error("Error finding work by friendly ID:", error);
      }
    }
  }

  const id = uuidv4();
  const { data, error } = await client
    .from("feedback")
    .insert({
      id,
      user_id: userId,
      work_id: workId,
      feedback_type: feedback.feedback_type,
      feedback_content: feedback.feedback_content,
      metadata: feedback.metadata || {},
      location_url: feedback.location_url,
      location_page: feedback.location_page,
      location_element: feedback.location_element,
      star_rating: feedback.star_rating,
      is_issue: feedback.is_issue || false,
    })
    .select("*")
    .single();

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error("Failed to create feedback");
  }

  return WorkFactory.feedbackToJSON(data);
}

/**
 * Retrieve all feedback entries submitted by a user.
 *
 * @param client - Supabase client
 * @param userId - User whose feedback should be returned
 * @returns Array of feedback objects sorted by creation date
 */
export async function getFeedbackForUser(client: SupabaseClient, userId: string): Promise<Feedback[]> {
  const { data, error } = await client
    .from("feedback")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }

  return (data || []).map(WorkFactory.feedbackToJSON);
}

/**
 * Fetch a single feedback entry by id for a user.
 *
 * @param client - Supabase client
 * @param userId - Feedback owner
 * @param feedbackId - Identifier of the feedback
 * @returns Feedback record or null
 */
export async function getFeedbackById(
  client: SupabaseClient,
  userId: string,
  feedbackId: string
): Promise<Feedback | null> {
  const { data, error } = await client
    .from("feedback")
    .select("*")
    .eq("id", feedbackId)
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data ? WorkFactory.feedbackToJSON(data) : null;
}

/**
 * Modify an existing feedback entry.
 *
 * @param client - Supabase client
 * @param userId - Owner of the feedback
 * @param feedbackId - ID of the record to update
 * @param updates - Partial fields to update
 * @returns Updated feedback record
 */
export async function updateFeedback(
  client: SupabaseClient,
  userId: string,
  feedbackId: string,
  updates: Partial<Omit<Feedback, "id" | "user_id" | "created_at">>
): Promise<Feedback> {
  const { data, error } = await client
    .from("feedback")
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq("id", feedbackId)
    .eq("user_id", userId)
    .select("*")
    .single();

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error("Failed to update feedback");
  }

  return WorkFactory.feedbackToJSON(data);
}

/**
 * Remove a feedback entry from the database.
 *
 * @param client - Supabase client
 * @param userId - Feedback owner
 * @param feedbackId - Feedback identifier
 */
export async function deleteFeedback(client: SupabaseClient, userId: string, feedbackId: string): Promise<void> {
  const { error } = await client
    .from("feedback")
    .delete()
    .eq("id", feedbackId)
    .eq("user_id", userId);

  if (error) {
    throw error;
  }
}

/**
 * Retrieve feedback items for a specific page and optional element.
 *
 * @param client - Supabase client
 * @param locationPage - Page identifier
 * @param locationElement - Optional page element selector
 * @returns Array of feedback objects
 */
export async function getFeedbackByLocation(
  client: SupabaseClient,
  locationPage: string,
  locationElement?: string
): Promise<Feedback[]> {
  let query = client
    .from("feedback")
    .select("*")
    .eq("location_page", locationPage);

  if (locationElement) {
    query = query.eq("location_element", locationElement);
  }

  const { data, error } = await query.order("created_at", { ascending: false });

  if (error) {
    throw error;
  }

  return (data || []).map(WorkFactory.feedbackToJSON);
}

/**
 * Calculate the average star rating for a specific analysis.
 *
 * @param client - Supabase client
 * @param analysisId - ID of the analysis
 * @returns Average rating and total count
 */
export async function getAnalysisFeedbackStats(
  client: SupabaseClient,
  analysisId: string
): Promise<{ averageRating: number; totalFeedback: number }> {
  const { data, error } = await client
    .from("feedback")
    .select("star_rating")
    .eq("location_element", "analysis")
    .eq("metadata->analysisId", analysisId)
    .not("star_rating", "is", null);

  if (error) {
    throw error;
  }

  const feedbackWithRatings = data || [];
  const totalFeedback = feedbackWithRatings.length;

  if (totalFeedback === 0) {
    return { averageRating: 0, totalFeedback: 0 };
  }

  const sum = feedbackWithRatings.reduce(
    (acc, item) => acc + (item.star_rating || 0),
    0
  );
  const averageRating = sum / totalFeedback;

  return {
    averageRating: Math.round(averageRating * 10) / 10,
    totalFeedback,
  };
}

